import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Stock,
  StockCategory,
  StockMovement,
  StockUnit,
  StockType,
  StockMovementType,
} from "../types";
import {
  getStocks,
  getStockUnits,
  createStockUnit,
  updateStockUnit,
  deleteStockUnit,
  getStockCategories,
  createStock,
  updateStock,
  deleteStock,
  createStockCategory,
  updateStockCategory,
  deleteStockCategory,
  getStockMovements,
  getStockMovementsByStock,
  createStockMovement,
  getStockSuppliers,
  quickAddStockSupplier,
} from "../services/api";
import { formatCurrency, formatNumber, turkishIncludes } from "../utils/formatters";
import { useAuth } from "../context/AuthContext";
import { usePermissions } from "../context/PermissionContext";
import PageLoader from "../components/PageLoader";
import AlertModal from "../components/AlertModal";
import ConfirmModal from "../components/ConfirmModal";
import "./StockManagement.css";

/**
 * Stok Yönetimi — stok kartları, kategoriler, birimler, hareketler ve kritik seviye.
 * Kurumsal lacivert çizgi (sm-*), Ürün Ağacı / Kesilen Belgeler ile aynı dil.
 *
 * Veri: stok/kategori/birim tek seferde yüklenir, süzme istemcide yapılır.
 * Hareketler sekme açılınca yüklenir. Kritik seviye sunucuyla aynı kuralla
 * (mevcut ≤ minimum) istemcide hesaplanır; ayrı istek gerekmez.
 */

/* ---------- sabitler ---------- */

type Tab = "stocks" | "categories" | "units" | "movements" | "critical";

const TYPE: Record<StockType, { label: string; cls: string }> = {
  [StockType.Hammadde]: { label: "Hammadde", cls: "raw" },
  [StockType.YariMamul]: { label: "Yarı mamül", cls: "semi" },
  [StockType.Mamul]: { label: "Mamül", cls: "fin" },
};

const MOVE: Record<StockMovementType, { label: string; tone: string; sign: -1 | 0 | 1; hint: string }> = {
  [StockMovementType.Giris]: { label: "Giriş", tone: "in", sign: 1, hint: "Mevcut stoğa eklenir." },
  [StockMovementType.Cikis]: { label: "Çıkış", tone: "out", sign: -1, hint: "Mevcut stoktan düşülür; yetersizse kayıt reddedilir." },
  [StockMovementType.Transfer]: { label: "Transfer", tone: "neutral", sign: 0, hint: "Depolar arası taşıma; toplam miktar değişmez." },
  [StockMovementType.Sayim]: { label: "Sayım", tone: "count", sign: 0, hint: "Girilen miktar YENİ TOPLAM olarak yazılır." },
  [StockMovementType.Iade]: { label: "İade", tone: "in", sign: 1, hint: "Geri dönen mal stoğa eklenir." },
  [StockMovementType.Fire]: { label: "Fire", tone: "out", sign: -1, hint: "Zayi olan miktar stoktan düşülür." },
};

const CURRENCIES = ["TRY", "USD", "EUR", "GBP"];

/* ---------- yardımcılar ---------- */

const qty = (n?: number | null) => formatNumber(n ?? 0, 3);
const money = (v?: number | null, cur?: string | null) => (v == null ? "—" : formatCurrency(v, cur || "TRY"));
const isCritical = (s: Stock) => s.currentStock <= (s.minimumStock ?? 0);
const isOut = (s: Stock) => s.currentStock <= 0;

/** Seviye çubuğu yüzdesi: maksimum varsa ona, yoksa minimumun iki katına göre. */
const levelPct = (s: Stock) => {
  const max = s.maximumStock && s.maximumStock > 0 ? s.maximumStock : (s.minimumStock ?? 0) * 2;
  if (!max || max <= 0) return s.currentStock > 0 ? 100 : 0;
  return Math.max(0, Math.min(100, (s.currentStock / max) * 100));
};
const levelTone = (s: Stock) => (isOut(s) ? "out" : isCritical(s) ? "low" : "ok");

const localNow = () => {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};
const fmtDate = (s?: string | null) => (s ? new Date(s).toLocaleDateString("tr-TR") : "—");
const fmtTime = (s?: string | null) => (s ? new Date(s).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" }) : "");
const dayKey = (s: string) => s.slice(0, 10);

const errorText = (error: any, fallback: string) => {
  const d = error?.response?.data;
  if (typeof d === "string") return d === "Insufficient stock" ? "Yetersiz stok: çıkış miktarı mevcut stoktan büyük." : d;
  return d?.detail || d?.title || d?.message || fallback;
};

const unitName = (u: StockUnit) => u.unitName || (u as any).name || "";

/* ---------- ikonlar ---------- */

const I = {
  search: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>,
  plus: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>,
  refresh: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 1 1-2.64-6.36" /><path d="M21 3v6h-6" /></svg>,
  close: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6 6 18" /></svg>,
  eye: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" /><circle cx="12" cy="12" r="3" /></svg>,
  edit: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></svg>,
  trash: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" /></svg>,
  swap: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M7 4v12" /><path d="m3 12 4 4 4-4" /><path d="M17 20V8" /><path d="m13 12 4-4 4 4" /></svg>,
  box: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 8l9-4 9 4v9l-9 4-9-4z" /><path d="M3 8l9 4 9-4M12 12v9" /></svg>,
  alert: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3 2 20h20L12 3Z" /><path d="M12 10v4" /><path d="M12 17h.01" /></svg>,
  filterOff: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 5h18l-7 8v6l-4-2v-4L3 5Z" /></svg>,
  check: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m5 12 4 4L19 6" /></svg>,
  inbox: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16v16H4z" /><path d="M4 13h5l2 3h2l2-3h5" /></svg>,
  arrowIn: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 4v13" /><path d="m6 11 6 6 6-6" /><path d="M5 21h14" /></svg>,
};

const Badge: React.FC<{ cls: string; children: React.ReactNode; title?: string }> = ({ cls, children, title }) => (
  <span className={`sm-badge ${cls}`} title={title}>{children}</span>
);

const Field: React.FC<{ label: string; hint?: string; span?: 2 | 3; children: React.ReactNode }> = ({ label, hint, span, children }) => (
  <label className={`sm-field ${span ? `span${span}` : ""}`}>
    <span>{label}{hint && <em>{hint}</em>}</span>
    {children}
  </label>
);

const Level: React.FC<{ s: Stock; wide?: boolean }> = ({ s, wide }) => (
  <div className={`sm-level ${levelTone(s)} ${wide ? "wide" : ""}`} title={`Minimum ${qty(s.minimumStock)}${s.maximumStock ? ` · Maksimum ${qty(s.maximumStock)}` : ""}`}>
    <i style={{ width: `${levelPct(s)}%` }} />
  </div>
);

/* ---------- formlar ---------- */

const emptyStock = (categoryId = "", unit = "") => ({
  productCode: "", productName: "", description: "", categoryId, stockType: StockType.Hammadde, unit,
  currentStock: 0, minimumStock: 0, maximumStock: 0, unitPrice: 0, currency: "TRY",
  warehouseLocation: "", supplier: "", supplierId: "" as string, color: "", size: "", barcode: "",
});
type StockForm = ReturnType<typeof emptyStock>;
type SupplierOption = { supplierId: string; name: string; code?: string | null; stockCount: number };

const emptyMove = (user: string, s?: Stock) => ({
  stockId: s?.id || "", movementType: StockMovementType.Giris, quantity: 0,
  unitPrice: s?.unitPrice || 0, currency: s?.currency || "TRY",
  referenceNo: "", referenceType: "", description: "", movementDate: localNow(),
  movedBy: user, fromWarehouse: s?.warehouseLocation || "", toWarehouse: "",
});
type MoveForm = ReturnType<typeof emptyMove>;

/* ---------- sayfa ---------- */

const StockManagement: React.FC = () => {
  const { has } = usePermissions();
  const { user } = useAuth();
  const userName = user ? `${user.firstName} ${user.lastName}`.trim() : "";
  const can = (tab: "stocks" | "categories" | "units" | "movements", op: "view" | "create" | "update" | "delete") =>
    has(`stock-management:${tab}:${op}`);

  const [tab, setTab] = useState<Tab>("stocks");
  const [stocks, setStocks] = useState<Stock[]>([]);
  const [categories, setCategories] = useState<StockCategory[]>([]);
  const [units, setUnits] = useState<StockUnit[]>([]);
  const [suppliers, setSuppliers] = useState<SupplierOption[]>([]);
  const [newSupplier, setNewSupplier] = useState<{ open: boolean; name: string; busy: boolean }>({ open: false, name: "", busy: false });
  const [movements, setMovements] = useState<StockMovement[]>([]);
  const [movesLoaded, setMovesLoaded] = useState(false);
  const [initial, setInitial] = useState(true);
  const [busy, setBusy] = useState(false);

  const [alert, setAlert] = useState<{ isOpen: boolean; message: string; type: "info" | "success" | "warning" | "error" }>({ isOpen: false, message: "", type: "info" });
  const [confirm, setConfirm] = useState<{ isOpen: boolean; message: string; onConfirm: () => void }>({ isOpen: false, message: "", onConfirm: () => {} });
  const ok = (message: string) => setAlert({ isOpen: true, message, type: "success" });
  const fail = (message: string) => setAlert({ isOpen: true, message, type: "error" });

  // Süzgeçler
  const [q, setQ] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState<StockType | "">("");
  const [onlyCritical, setOnlyCritical] = useState(false);
  const [mq, setMq] = useState("");
  const [mType, setMType] = useState<StockMovementType | "">("");
  const [mFrom, setMFrom] = useState("");
  const [mTo, setMTo] = useState("");

  // Çekmece
  const [detail, setDetail] = useState<Stock | null>(null);
  const [detailMoves, setDetailMoves] = useState<StockMovement[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);

  // Modallar
  const [stockModal, setStockModal] = useState<{ open: boolean; editing: Stock | null; form: StockForm }>({ open: false, editing: null, form: emptyStock() });
  const [catModal, setCatModal] = useState<{ open: boolean; editing: StockCategory | null; name: string; description: string }>({ open: false, editing: null, name: "", description: "" });
  const [unitModal, setUnitModal] = useState<{ open: boolean; editing: StockUnit | null; unitName: string; abbreviation: string; sortOrder: number }>({ open: false, editing: null, unitName: "", abbreviation: "", sortOrder: 0 });
  const [moveModal, setMoveModal] = useState<{ open: boolean; form: MoveForm }>({ open: false, form: emptyMove("") });

  /* ---- yükleme ---- */

  const loadData = useCallback(async () => {
    try {
      const [s, c, u, sup] = await Promise.all([getStocks(), getStockCategories(), getStockUnits(), getStockSuppliers().catch(() => ({ data: [] as SupplierOption[] }))]);
      setStocks(s.data || []);
      setCategories(c.data || []);
      setUnits((u.data || []).filter((x: StockUnit) => x.isActive));
      setSuppliers(sup.data || []);
    } catch {
      fail("Stok verileri yüklenemedi.");
    } finally {
      setInitial(false);
    }
  }, []);

  const loadMovements = useCallback(async () => {
    try {
      const r = await getStockMovements();
      setMovements(r.data || []);
      setMovesLoaded(true);
    } catch {
      fail("Hareketler yüklenemedi.");
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { if (tab === "movements" && !movesLoaded) loadMovements(); }, [tab, movesLoaded, loadMovements]);

  const refreshAll = async () => {
    setBusy(true);
    await loadData();
    if (movesLoaded) await loadMovements();
    if (detail) await openDetail(detail, true);
    setBusy(false);
  };

  /* ---- türetilmiş ---- */

  const stockById = useMemo(() => new Map(stocks.map((s) => [s.id, s])), [stocks]);
  const catName = (id?: string) => categories.find((c) => c.id === id)?.name || "—";
  const critical = useMemo(() => stocks.filter((s) => s.isActive !== false && isCritical(s)).sort((a, b) => a.currentStock - b.currentStock), [stocks]);

  const filteredStocks = useMemo(() => stocks.filter((s) => {
    if (catFilter && s.categoryId !== catFilter) return false;
    if (typeFilter !== "" && (s.stockType ?? StockType.Hammadde) !== typeFilter) return false;
    if (onlyCritical && !isCritical(s)) return false;
    if (q) {
      const hit = turkishIncludes(s.productCode, q) || turkishIncludes(s.productName, q) || turkishIncludes(s.description || "", q)
        || turkishIncludes(s.barcode || "", q) || turkishIncludes(s.supplierName || s.supplier || "", q) || turkishIncludes(catName(s.categoryId), q);
      if (!hit) return false;
    }
    return true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [stocks, catFilter, typeFilter, onlyCritical, q, categories]);

  const filteredCats = useMemo(() => categories.filter((c) => !q || turkishIncludes(c.name, q) || turkishIncludes(c.description || "", q)), [categories, q]);
  const filteredUnits = useMemo(() => units.filter((u) => !q || turkishIncludes(unitName(u), q) || turkishIncludes(u.abbreviation || "", q)), [units, q]);

  const filteredMoves = useMemo(() => movements.filter((m) => {
    if (mType !== "" && m.movementType !== mType) return false;
    const day = dayKey(m.movementDate);
    if (mFrom && day < mFrom) return false;
    if (mTo && day > mTo) return false;
    if (mq) {
      const s = m.stock || stockById.get(m.stockId);
      const hit = turkishIncludes(s?.productName || "", mq) || turkishIncludes(s?.productCode || "", mq)
        || turkishIncludes(m.referenceNo || "", mq) || turkishIncludes(m.description || "", mq) || turkishIncludes(m.movedBy || "", mq)
        || turkishIncludes(m.workshopName || "", mq);
      if (!hit) return false;
    }
    return true;
  }).sort((a, b) => new Date(b.movementDate).getTime() - new Date(a.movementDate).getTime()), [movements, mType, mFrom, mTo, mq, stockById]);

  const kpi = useMemo(() => {
    const byType: Record<number, number> = { 1: 0, 2: 0, 3: 0 };
    const value: Record<string, number> = {};
    for (const s of stocks) {
      byType[s.stockType ?? StockType.Hammadde] = (byType[s.stockType ?? StockType.Hammadde] || 0) + 1;
      if (s.unitPrice) { const c = s.currency || "TRY"; value[c] = (value[c] || 0) + s.currentStock * s.unitPrice; }
    }
    const currencies = Object.keys(value).sort((a, b) => (a === "TRY" ? -1 : b === "TRY" ? 1 : a.localeCompare(b)));
    return { total: stocks.length, byType, critical: critical.length, out: critical.filter(isOut).length, value, currencies };
  }, [stocks, critical]);

  const stockFilterOn = !!(q || catFilter || typeFilter !== "" || onlyCritical);
  const clearStockFilters = () => { setQ(""); setCatFilter(""); setTypeFilter(""); setOnlyCritical(false); };
  const moveFilterOn = !!(mq || mType !== "" || mFrom || mTo);
  const clearMoveFilters = () => { setMq(""); setMType(""); setMFrom(""); setMTo(""); };

  /* ---- çekmece ---- */

  const openDetail = async (s: Stock, silent = false) => {
    setDetail(s);
    if (!silent) setDetailLoading(true);
    try { setDetailMoves(((await getStockMovementsByStock(s.id)).data || []).slice().sort((a: StockMovement, b: StockMovement) => new Date(b.movementDate).getTime() - new Date(a.movementDate).getTime())); }
    catch { setDetailMoves([]); }
    finally { setDetailLoading(false); }
  };
  useEffect(() => {
    if (!detail) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setDetail(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [detail]);

  /* ---- stok kartı ---- */

  const openNewStock = () => setStockModal({ open: true, editing: null, form: emptyStock(categories[0]?.id || "", unitName(units[0]) || "adet") });
  const openEditStock = (s: Stock) => setStockModal({
    open: true, editing: s,
    form: {
      productCode: s.productCode, productName: s.productName, description: s.description || "", categoryId: s.categoryId,
      stockType: s.stockType || StockType.Hammadde, unit: s.unit, currentStock: s.currentStock, minimumStock: s.minimumStock || 0,
      maximumStock: s.maximumStock || 0, unitPrice: s.unitPrice || 0, currency: s.currency || "TRY", warehouseLocation: s.warehouseLocation || "",
      supplier: s.supplier || "", supplierId: s.supplierId || "", color: s.color || "", size: s.size || "", barcode: s.barcode || "",
    },
  });
  const setSF = (patch: Partial<StockForm>) => setStockModal((m) => ({ ...m, form: { ...m.form, ...patch } }));

  const addSupplier = async () => {
    const name = newSupplier.name.trim();
    if (name.length < 2) return;
    setNewSupplier((n) => ({ ...n, busy: true }));
    try {
      const r = (await quickAddStockSupplier(name)).data;
      setSuppliers((list) => list.some((x) => x.supplierId === r.supplierId) ? list : [...list, { supplierId: r.supplierId, name: r.name, code: r.code, stockCount: 0 }].sort((a, b) => a.name.localeCompare(b.name, "tr")));
      setSF({ supplierId: r.supplierId, supplier: r.name });
      setNewSupplier({ open: false, name: "", busy: false });
      ok(r.existed ? "Bu tedarikçi zaten kayıtlıydı; karta bağlandı." : "Tedarikçi oluşturuldu ve karta bağlandı.");
    } catch (err) { fail(errorText(err, "Tedarikçi eklenemedi.")); setNewSupplier((n) => ({ ...n, busy: false })); }
  };

  const saveStock = async (e: React.FormEvent) => {
    e.preventDefault();
    const f = stockModal.form;
    if (!f.productCode.trim() || !f.productName.trim()) { setAlert({ isOpen: true, message: "Ürün kodu ve adı zorunludur.", type: "warning" }); return; }
    if (!f.categoryId) { setAlert({ isOpen: true, message: "Önce bir kategori seçin. Kategori yoksa Kategoriler sekmesinden ekleyin.", type: "warning" }); return; }
    if (f.maximumStock > 0 && f.maximumStock < f.minimumStock) { setAlert({ isOpen: true, message: "Maksimum stok minimumdan küçük olamaz.", type: "warning" }); return; }
    setBusy(true);
    try {
      const payload = { ...f, supplierId: f.supplierId || null };
      if (stockModal.editing) { await updateStock(stockModal.editing.id, { id: stockModal.editing.id, ...payload }); ok("Stok kartı güncellendi."); }
      else { await createStock(payload); ok("Stok kartı oluşturuldu."); }
      setStockModal((m) => ({ ...m, open: false }));
      await loadData();
      if (detail && stockModal.editing?.id === detail.id) setDetail((d) => d ? { ...d, ...f } as Stock : d);
    } catch (err) { fail(errorText(err, "Stok kartı kaydedilemedi.")); }
    finally { setBusy(false); }
  };

  const removeStock = (s: Stock) => setConfirm({
    isOpen: true, message: `"${s.productName}" stok kartı silinecek. Hareket geçmişi kalır, kart listeden kalkar.`,
    onConfirm: async () => {
      setConfirm((c) => ({ ...c, isOpen: false })); setBusy(true);
      try { await deleteStock(s.id); ok("Stok kartı silindi."); if (detail?.id === s.id) setDetail(null); await loadData(); }
      catch (err) { fail(errorText(err, "Stok kartı silinemedi.")); }
      finally { setBusy(false); }
    },
  });

  /* ---- kategori ---- */

  const saveCat = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!catModal.name.trim()) { setAlert({ isOpen: true, message: "Kategori adı zorunludur.", type: "warning" }); return; }
    setBusy(true);
    try {
      if (catModal.editing) { await updateStockCategory(catModal.editing.id, { id: catModal.editing.id, name: catModal.name.trim(), description: catModal.description, isActive: true }); ok("Kategori güncellendi."); }
      else { await createStockCategory({ name: catModal.name.trim(), description: catModal.description }); ok("Kategori oluşturuldu."); }
      setCatModal((m) => ({ ...m, open: false }));
      await loadData();
    } catch (err) { fail(errorText(err, "Kategori kaydedilemedi.")); }
    finally { setBusy(false); }
  };
  const removeCat = (c: StockCategory) => {
    const used = stocks.filter((s) => s.categoryId === c.id).length;
    setConfirm({
      isOpen: true,
      message: used ? `"${c.name}" kategorisine bağlı ${used} stok kartı var. Yine de silinsin mi?` : `"${c.name}" kategorisi silinecek.`,
      onConfirm: async () => {
        setConfirm((x) => ({ ...x, isOpen: false })); setBusy(true);
        try { await deleteStockCategory(c.id); ok("Kategori silindi."); await loadData(); }
        catch (err) { fail(errorText(err, "Kategori silinemedi.")); }
        finally { setBusy(false); }
      },
    });
  };

  /* ---- birim ---- */

  const saveUnit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!unitModal.unitName.trim()) { setAlert({ isOpen: true, message: "Birim adı zorunludur.", type: "warning" }); return; }
    setBusy(true);
    const data = { unitName: unitModal.unitName.trim(), abbreviation: unitModal.abbreviation.trim() || undefined, sortOrder: unitModal.sortOrder };
    try {
      if (unitModal.editing) { await updateStockUnit(unitModal.editing.id, { ...data, id: unitModal.editing.id }); ok("Birim güncellendi."); }
      else { await createStockUnit(data); ok("Birim oluşturuldu."); }
      setUnitModal((m) => ({ ...m, open: false }));
      await loadData();
    } catch (err) { fail(errorText(err, "Birim kaydedilemedi.")); }
    finally { setBusy(false); }
  };
  const removeUnit = (u: StockUnit) => setConfirm({
    isOpen: true, message: `"${unitName(u)}" birimi silinecek. Bu birimi kullanan kartlar etkilenmez.`,
    onConfirm: async () => {
      setConfirm((x) => ({ ...x, isOpen: false })); setBusy(true);
      try { await deleteStockUnit(u.id); ok("Birim silindi."); await loadData(); }
      catch (err) { fail(errorText(err, "Birim silinemedi.")); }
      finally { setBusy(false); }
    },
  });

  /* ---- hareket ---- */

  const openMove = (s?: Stock, type: StockMovementType = StockMovementType.Giris) =>
    setMoveModal({ open: true, form: { ...emptyMove(userName, s), movementType: type } });
  const setMF = (patch: Partial<MoveForm>) => setMoveModal((m) => ({ ...m, form: { ...m.form, ...patch } }));

  const moveStock = stockById.get(moveModal.form.stockId);
  const movePreview = useMemo(() => {
    const f = moveModal.form; const s = moveStock;
    if (!s) return null;
    const cur = s.currentStock; const n = Number(f.quantity) || 0;
    let after = cur;
    if (f.movementType === StockMovementType.Giris || f.movementType === StockMovementType.Iade) after = cur + n;
    else if (f.movementType === StockMovementType.Cikis || f.movementType === StockMovementType.Fire) after = cur - n;
    else if (f.movementType === StockMovementType.Sayim) after = n;
    return { cur, after, insufficient: after < 0, unit: s.unit };
  }, [moveModal.form, moveStock]);

  const saveMove = async (e: React.FormEvent) => {
    e.preventDefault();
    const f = moveModal.form;
    if (!f.stockId) { setAlert({ isOpen: true, message: "Bir stok kartı seçin.", type: "warning" }); return; }
    if (!(Number(f.quantity) > 0) && f.movementType !== StockMovementType.Sayim) { setAlert({ isOpen: true, message: "Miktar sıfırdan büyük olmalı.", type: "warning" }); return; }
    if (movePreview?.insufficient) { setAlert({ isOpen: true, message: "Yetersiz stok: çıkış miktarı mevcut stoktan büyük.", type: "warning" }); return; }
    setBusy(true);
    try {
      await createStockMovement({ ...f, quantity: Number(f.quantity), unitPrice: Number(f.unitPrice) || 0, totalPrice: (Number(f.quantity) || 0) * (Number(f.unitPrice) || 0) });
      ok("Stok hareketi kaydedildi.");
      setMoveModal((m) => ({ ...m, open: false }));
      await loadData();
      if (movesLoaded) await loadMovements();
      if (detail?.id === f.stockId) {
        const fresh = (await getStocks()).data?.find((x: Stock) => x.id === f.stockId);
        if (fresh) await openDetail(fresh, true);
      }
    } catch (err) { fail(errorText(err, "Hareket kaydedilemedi.")); }
    finally { setBusy(false); }
  };

  if (initial) return <PageLoader message="Stok verileri yükleniyor..." />;

  /* ---------- görünüm parçaları ---------- */

  const tabs: { key: Tab; label: string; count?: number; show: boolean; tone?: string }[] = [
    { key: "stocks", label: "Stok kartları", count: stocks.length, show: can("stocks", "view") },
    { key: "critical", label: "Kritik seviye", count: critical.length, show: can("stocks", "view"), tone: critical.length ? "danger" : "" },
    { key: "movements", label: "Hareketler", count: movesLoaded ? movements.length : undefined, show: can("movements", "view") },
    { key: "categories", label: "Kategoriler", count: categories.length, show: can("categories", "view") },
    { key: "units", label: "Birimler", count: units.length, show: can("units", "view") },
  ];

  const primaryAction = () => {
    if (tab === "categories" && can("categories", "create")) return <button className="sm-btn primary" onClick={() => setCatModal({ open: true, editing: null, name: "", description: "" })}>{I.plus} Yeni kategori</button>;
    if (tab === "units" && can("units", "create")) return <button className="sm-btn primary" onClick={() => setUnitModal({ open: true, editing: null, unitName: "", abbreviation: "", sortOrder: units.length + 1 })}>{I.plus} Yeni birim</button>;
    if (tab === "movements" && can("movements", "create")) return <button className="sm-btn primary" onClick={() => openMove()}>{I.swap} Yeni hareket</button>;
    if ((tab === "stocks" || tab === "critical") && can("stocks", "create")) return <button className="sm-btn primary" onClick={openNewStock}>{I.plus} Yeni stok kartı</button>;
    return null;
  };

  const rowActions = (s: Stock) => (
    <div className="sm-rowacts" onClick={(e) => e.stopPropagation()}>
      <button className="sm-ib" title="Detay" onClick={() => openDetail(s)}>{I.eye}</button>
      {can("movements", "create") && <button className="sm-ib" title="Hareket ekle" onClick={() => openMove(s)}>{I.swap}</button>}
      {can("stocks", "update") && <button className="sm-ib" title="Düzenle" onClick={() => openEditStock(s)}>{I.edit}</button>}
      {can("stocks", "delete") && <button className="sm-ib danger" title="Sil" onClick={() => removeStock(s)}>{I.trash}</button>}
    </div>
  );

  const stockRow = (s: Stock, mode: "list" | "critical") => (
    <tr key={s.id} className={`${detail?.id === s.id ? "on" : ""} ${isOut(s) ? "out" : isCritical(s) ? "low" : ""}`} onClick={() => openDetail(s)}>
      <td>
        <div className="sm-code">{s.productCode}</div>
        <div className="sm-name">{s.productName}</div>
        {(s.color || s.size) && <div className="sm-sub">{[s.color, s.size].filter(Boolean).join(" · ")}</div>}
      </td>
      <td className="hide-md"><Badge cls={TYPE[s.stockType ?? StockType.Hammadde].cls}>{TYPE[s.stockType ?? StockType.Hammadde].label}</Badge></td>
      <td className="hide-md">{catName(s.categoryId)}</td>
      <td className="num">
        <div className={`sm-qty ${levelTone(s)}`}>{qty(s.currentStock)} <small>{s.unit}</small></div>
        <Level s={s} />
      </td>
      {mode === "critical" ? (
        <>
          <td className="num">{qty(s.minimumStock)} <small className="sm-sub">{s.unit}</small></td>
          <td className="num"><span className="sm-neg">−{qty((s.minimumStock ?? 0) - s.currentStock)}</span> <small className="sm-sub">{s.unit}</small></td>
          <td className="hide-sm">{s.supplierName || s.supplier || "—"}</td>
          <td className="act">
            <div className="sm-rowacts always" onClick={(e) => e.stopPropagation()}>
              {can("movements", "create") && <button className="sm-btn small" onClick={() => openMove(s)}>{I.arrowIn} Giriş yap</button>}
              <button className="sm-ib" title="Detay" onClick={() => openDetail(s)}>{I.eye}</button>
            </div>
          </td>
        </>
      ) : (
        <>
          <td className="num hide-sm">{s.unitPrice ? money(s.unitPrice, s.currency) : "—"}</td>
          <td className="num hide-md">{s.unitPrice ? money(s.currentStock * s.unitPrice, s.currency) : "—"}</td>
          <td className="hide-lg">{s.warehouseLocation || "—"}</td>
          <td className="act">{rowActions(s)}</td>
        </>
      )}
    </tr>
  );

  const moveRow = (m: StockMovement, showStock: boolean) => {
    const mv = MOVE[m.movementType] || MOVE[StockMovementType.Giris];
    const s = m.stock || stockById.get(m.stockId);
    return (
      <tr key={m.id} className="static">
        <td className="nowrap"><div>{fmtDate(m.movementDate)}</div><div className="sm-sub">{fmtTime(m.movementDate)}</div></td>
        {showStock && <td><div className="sm-name">{s?.productName || "Silinmiş kart"}</div><div className="sm-sub">{s?.productCode || m.stockId.slice(0, 8)}</div></td>}
        <td><Badge cls={`mv-${mv.tone}`}>{mv.label}</Badge></td>
        <td className={`num sm-signed ${mv.tone}`}>{mv.sign > 0 ? "+" : mv.sign < 0 ? "−" : mv.tone === "count" ? "=" : ""}{qty(m.quantity)} <small>{s?.unit || ""}</small></td>
        <td className="num hide-md">{m.unitPrice ? money(m.unitPrice, m.currency) : "—"}</td>
        <td className="num hide-sm">{m.totalPrice ? money(m.totalPrice, m.currency) : "—"}</td>
        <td className="hide-md">{m.referenceNo ? <><div>{m.referenceNo}</div>{m.referenceType && <div className="sm-sub">{m.referenceType}</div>}</> : "—"}</td>
        {/* Atölye yalnız maliyet girişinden doğan hareketlerde dolu: malzeme hangi atölyede harcandı */}
        <td className="hide-md">{m.workshopName ? <span className="sm-workshop">{m.workshopName}</span> : "—"}</td>
        <td className="hide-lg">{m.movedBy || "—"}</td>
        <td className="hide-lg sm-desc" title={m.description || ""}>{m.description || "—"}</td>
      </tr>
    );
  };

  const blank = (title: string, text: string, action?: React.ReactNode) => (
    <div className="sm-blank"><div className="sm-blank-icon">{I.inbox}</div><b>{title}</b>{text}{action}</div>
  );

  /* ---------- görünüm ---------- */

  return (
    <div className="sm-page">
      <header className="sm-head">
        <div>
          <span className="sm-kicker">Stok</span>
          <h1>Stok Yönetimi</h1>
          <p>Hammadde, yarı mamül ve mamül kartları; giriş, çıkış, sayım ve fire hareketleri; kritik seviye takibi.</p>
        </div>
        <div className="sm-head-actions">
          <button className="sm-btn" onClick={refreshAll} disabled={busy}><span className={busy ? "sm-spin-icon" : ""}>{I.refresh}</span> Yenile</button>
          {tab !== "movements" && can("movements", "create") && stocks.length > 0 && <button className="sm-btn" onClick={() => openMove()}>{I.swap} Hareket ekle</button>}
          {primaryAction()}
        </div>
      </header>

      <section className="sm-kpis">
        <div className="sm-kpi navy"><span className="l">Stok kartı</span><span className="v">{kpi.total}</span><span className="s">{kpi.byType[1]} hammadde · {kpi.byType[2]} yarı mamül · {kpi.byType[3]} mamül</span></div>
        <div className={`sm-kpi ${kpi.critical ? "err" : "ok"}`}><span className="l">Kritik seviye</span><span className="v">{kpi.critical}</span><span className="s">{kpi.critical ? `${kpi.out} tükendi · ${kpi.critical - kpi.out} minimum altında` : "tüm kartlar yeterli"}</span></div>
        <div className="sm-kpi"><span className="l">Stok değeri</span><span className="v">{kpi.currencies.length ? money(kpi.value[kpi.currencies[0]], kpi.currencies[0]) : "—"}</span><span className="s">{kpi.currencies.length > 1 ? kpi.currencies.slice(1).map((c) => money(kpi.value[c], c)).join(" · ") : "mevcut × birim fiyat"}</span></div>
        <div className="sm-kpi"><span className="l">Tanımlar</span><span className="v">{categories.length} <small>/ {units.length}</small></span><span className="s">kategori / birim</span></div>
      </section>

      <nav className="sm-tabs" aria-label="Bölümler">
        {tabs.filter((t) => t.show).map((t) => (
          <button key={t.key} className={`${tab === t.key ? "on" : ""} ${t.tone || ""}`} onClick={() => setTab(t.key)}>
            {t.label}{t.count !== undefined && <span>{t.count}</span>}
          </button>
        ))}
      </nav>

      {/* ===== Stok kartları ===== */}
      {tab === "stocks" && (
        <section className={`sm-card ${busy ? "dim" : ""}`}>
          <div className="sm-toolbar">
            <div className="sm-search">{I.search}<input placeholder="Kod, ad, barkod, tedarikçi veya kategori ara" value={q} onChange={(e) => setQ(e.target.value)} />{q && <button className="sm-ib" aria-label="Temizle" onClick={() => setQ("")}>{I.close}</button>}</div>
            <select className="sm-select" value={catFilter} onChange={(e) => setCatFilter(e.target.value)}>
              <option value="">Tüm kategoriler</option>
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <div className="sm-chipgroup">
              <button className={typeFilter === "" ? "on" : ""} onClick={() => setTypeFilter("")}>Tümü</button>
              {([StockType.Hammadde, StockType.YariMamul, StockType.Mamul] as StockType[]).map((t) => (
                <button key={t} className={typeFilter === t ? "on" : ""} onClick={() => setTypeFilter(t)}>{TYPE[t].label}</button>
              ))}
            </div>
            <button className={`sm-chip ${onlyCritical ? "on danger" : ""}`} onClick={() => setOnlyCritical((v) => !v)}>{I.alert} Yalnızca kritik</button>
            {stockFilterOn && <button className="sm-clear" onClick={clearStockFilters}>{I.filterOff} Temizle</button>}
          </div>
          <div className="sm-card-head"><span><b>{filteredStocks.length}</b> kart{filteredStocks.length !== stocks.length && <em> / {stocks.length}</em>}</span><span className="sm-hint">Satıra tıklayınca hareket geçmişi açılır</span></div>
          {filteredStocks.length === 0 ? blank(
            stocks.length === 0 ? "Henüz stok kartı yok" : "Süzgeçle eşleşen kart yok",
            stocks.length === 0 ? "Hammadde, yarı mamül ve mamülleri kart olarak tanımlayın; hareketler bu kartlar üzerinde işlenir." : "Arama ya da süzgeçleri gevşetmeyi deneyin.",
            stocks.length === 0 && can("stocks", "create") ? <button className="sm-btn primary" onClick={openNewStock}>{I.plus} İlk stok kartını oluştur</button> : stockFilterOn ? <button className="sm-btn" onClick={clearStockFilters}>Süzgeçleri temizle</button> : null,
          ) : (
            <div className="sm-table-wrap">
              <table className="sm-table">
                <thead><tr><th>Stok</th><th className="hide-md">Tip</th><th className="hide-md">Kategori</th><th className="num">Mevcut</th><th className="num hide-sm">Birim fiyat</th><th className="num hide-md">Değer</th><th className="hide-lg">Depo</th><th className="act" /></tr></thead>
                <tbody>{filteredStocks.map((s) => stockRow(s, "list"))}</tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {/* ===== Kritik seviye ===== */}
      {tab === "critical" && (
        <section className={`sm-card ${busy ? "dim" : ""}`}>
          <div className="sm-card-head"><span><b>{critical.length}</b> kart minimum seviyede ya da altında</span><span className="sm-hint">Kural: mevcut ≤ minimum</span></div>
          {critical.length === 0 ? blank("Tüm kartlar yeterli seviyede", "Minimum stoğun altına düşen kart yok. Minimum girilmemiş kartlar yalnızca tükenince burada görünür.") : (
            <div className="sm-table-wrap">
              <table className="sm-table">
                <thead><tr><th>Stok</th><th className="hide-md">Tip</th><th className="hide-md">Kategori</th><th className="num">Mevcut</th><th className="num">Minimum</th><th className="num">Eksik</th><th className="hide-sm">Tedarikçi</th><th className="act" /></tr></thead>
                <tbody>{critical.map((s) => stockRow(s, "critical"))}</tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {/* ===== Hareketler ===== */}
      {tab === "movements" && (
        <section className={`sm-card ${busy ? "dim" : ""}`}>
          <div className="sm-toolbar">
            <div className="sm-search">{I.search}<input placeholder="Stok, referans, açıklama veya kişi ara" value={mq} onChange={(e) => setMq(e.target.value)} />{mq && <button className="sm-ib" aria-label="Temizle" onClick={() => setMq("")}>{I.close}</button>}</div>
            <div className="sm-chipgroup">
              <button className={mType === "" ? "on" : ""} onClick={() => setMType("")}>Tümü</button>
              {(Object.keys(MOVE).map(Number) as StockMovementType[]).map((t) => <button key={t} className={mType === t ? "on" : ""} onClick={() => setMType(t)}>{MOVE[t].label}</button>)}
            </div>
            <div className="sm-dates"><input type="date" value={mFrom} max={mTo || undefined} onChange={(e) => setMFrom(e.target.value)} /><span>–</span><input type="date" value={mTo} min={mFrom || undefined} onChange={(e) => setMTo(e.target.value)} /></div>
            {moveFilterOn && <button className="sm-clear" onClick={clearMoveFilters}>{I.filterOff} Temizle</button>}
          </div>
          <div className="sm-card-head"><span><b>{filteredMoves.length}</b> hareket{filteredMoves.length !== movements.length && <em> / {movements.length}</em>}</span><span className="sm-hint">Sayım hareketinde miktar yeni toplamdır</span></div>
          {!movesLoaded ? <div className="sm-loading"><span className="sm-spin" /> Hareketler yükleniyor</div>
            : filteredMoves.length === 0 ? blank(movements.length === 0 ? "Henüz hareket yok" : "Süzgeçle eşleşen hareket yok", movements.length === 0 ? "Giriş, çıkış, sayım ve fire kayıtları burada listelenir." : "Tarih aralığını ya da türü değiştirin.", movements.length === 0 && can("movements", "create") && stocks.length > 0 ? <button className="sm-btn primary" onClick={() => openMove()}>{I.swap} İlk hareketi kaydet</button> : null)
            : (
              <div className="sm-table-wrap">
                <table className="sm-table">
                  <thead><tr><th>Tarih</th><th>Stok</th><th>Tür</th><th className="num">Miktar</th><th className="num hide-md">Birim fiyat</th><th className="num hide-sm">Tutar</th><th className="hide-md">Referans</th><th className="hide-md">Atölye</th><th className="hide-lg">Kişi</th><th className="hide-lg">Açıklama</th></tr></thead>
                  <tbody>{filteredMoves.map((m) => moveRow(m, true))}</tbody>
                </table>
              </div>
            )}
        </section>
      )}

      {/* ===== Kategoriler ===== */}
      {tab === "categories" && (
        <section className={`sm-card ${busy ? "dim" : ""}`}>
          <div className="sm-toolbar"><div className="sm-search">{I.search}<input placeholder="Kategori ara" value={q} onChange={(e) => setQ(e.target.value)} />{q && <button className="sm-ib" aria-label="Temizle" onClick={() => setQ("")}>{I.close}</button>}</div></div>
          {filteredCats.length === 0 ? blank(categories.length === 0 ? "Henüz kategori yok" : "Eşleşen kategori yok", categories.length === 0 ? "Stok kartları bir kategoriye bağlanır; önce en az bir kategori tanımlayın." : "Aramayı değiştirin.", categories.length === 0 && can("categories", "create") ? <button className="sm-btn primary" onClick={() => setCatModal({ open: true, editing: null, name: "", description: "" })}>{I.plus} İlk kategoriyi oluştur</button> : null) : (
            <div className="sm-table-wrap">
              <table className="sm-table">
                <thead><tr><th>Kategori</th><th className="hide-sm">Açıklama</th><th className="num">Kart</th><th className="num hide-md">Kritik</th><th>Durum</th><th className="act" /></tr></thead>
                <tbody>
                  {filteredCats.map((c) => {
                    const mine = stocks.filter((s) => s.categoryId === c.id);
                    const crit = mine.filter(isCritical).length;
                    return (
                      <tr key={c.id} className="static">
                        <td><div className="sm-name">{c.name}</div></td>
                        <td className="hide-sm sm-desc">{c.description || "—"}</td>
                        <td className="num"><button className="sm-link" onClick={() => { setCatFilter(c.id); setTab("stocks"); }}>{mine.length}</button></td>
                        <td className="num hide-md">{crit ? <span className="sm-neg">{crit}</span> : "—"}</td>
                        <td><Badge cls={c.isActive ? "ok" : "muted"}>{c.isActive ? "Aktif" : "Pasif"}</Badge></td>
                        <td className="act"><div className="sm-rowacts always">
                          {can("categories", "update") && <button className="sm-ib" title="Düzenle" onClick={() => setCatModal({ open: true, editing: c, name: c.name, description: c.description || "" })}>{I.edit}</button>}
                          {can("categories", "delete") && <button className="sm-ib danger" title="Sil" onClick={() => removeCat(c)}>{I.trash}</button>}
                        </div></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {/* ===== Birimler ===== */}
      {tab === "units" && (
        <section className={`sm-card ${busy ? "dim" : ""}`}>
          <div className="sm-toolbar"><div className="sm-search">{I.search}<input placeholder="Birim ara" value={q} onChange={(e) => setQ(e.target.value)} />{q && <button className="sm-ib" aria-label="Temizle" onClick={() => setQ("")}>{I.close}</button>}</div></div>
          {filteredUnits.length === 0 ? blank(units.length === 0 ? "Henüz birim yok" : "Eşleşen birim yok", "Adet, metre, kilogram gibi ölçü birimleri stok kartlarında kullanılır.", units.length === 0 && can("units", "create") ? <button className="sm-btn primary" onClick={() => setUnitModal({ open: true, editing: null, unitName: "", abbreviation: "", sortOrder: 1 })}>{I.plus} İlk birimi oluştur</button> : null) : (
            <div className="sm-table-wrap">
              <table className="sm-table">
                <thead><tr><th className="num narrow">Sıra</th><th>Birim</th><th>Kısaltma</th><th className="num hide-sm">Kullanan kart</th><th>Durum</th><th className="act" /></tr></thead>
                <tbody>
                  {filteredUnits.slice().sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)).map((u) => (
                    <tr key={u.id} className="static">
                      <td className="num narrow sm-sub">{u.sortOrder ?? "—"}</td>
                      <td><div className="sm-name">{unitName(u)}</div></td>
                      <td>{u.abbreviation ? <code className="sm-code-pill">{u.abbreviation}</code> : "—"}</td>
                      <td className="num hide-sm">{stocks.filter((s) => s.unit === unitName(u) || s.unit === u.abbreviation).length || "—"}</td>
                      <td><Badge cls={u.isActive ? "ok" : "muted"}>{u.isActive ? "Aktif" : "Pasif"}</Badge></td>
                      <td className="act"><div className="sm-rowacts always">
                        {can("units", "update") && <button className="sm-ib" title="Düzenle" onClick={() => setUnitModal({ open: true, editing: u, unitName: unitName(u), abbreviation: u.abbreviation || "", sortOrder: u.sortOrder || 0 })}>{I.edit}</button>}
                        {can("units", "delete") && <button className="sm-ib danger" title="Sil" onClick={() => removeUnit(u)}>{I.trash}</button>}
                      </div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {/* ===== Detay çekmecesi ===== */}
      {detail && (
        <div className="sm-overlay" onClick={() => setDetail(null)}>
          <aside className="sm-drawer" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="sm-drawer-head">
              <div className="sm-drawer-title">
                <span className="sm-kicker">{TYPE[detail.stockType ?? StockType.Hammadde].label} · {catName(detail.categoryId)}</span>
                <h2>{detail.productName}</h2>
                <div className="sm-badges"><code className="sm-code-pill">{detail.productCode}</code>{detail.barcode && <code className="sm-code-pill muted">{detail.barcode}</code>}{isOut(detail) ? <Badge cls="err">Tükendi</Badge> : isCritical(detail) ? <Badge cls="warn">Kritik seviye</Badge> : <Badge cls="ok">Yeterli</Badge>}</div>
              </div>
              <button className="sm-ib big" aria-label="Kapat" onClick={() => setDetail(null)}>{I.close}</button>
            </div>
            <div className="sm-drawer-body">
              <div className="sm-levelcard">
                <div className="sm-levelcard-top"><span>Mevcut</span><b className={levelTone(detail)}>{qty(detail.currentStock)} {detail.unit}</b></div>
                <Level s={detail} wide />
                <div className="sm-levelcard-foot"><span>Min {qty(detail.minimumStock)}</span>{!!detail.maximumStock && <span>Maks {qty(detail.maximumStock)}</span>}<span>{detail.unitPrice ? `Değer ${money(detail.currentStock * detail.unitPrice, detail.currency)}` : "birim fiyat girilmemiş"}</span></div>
              </div>
              <dl className="sm-facts">
                <div><dt>Birim fiyat</dt><dd>{detail.unitPrice ? money(detail.unitPrice, detail.currency) : "—"}</dd></div>
                <div><dt>Depo</dt><dd>{detail.warehouseLocation || "—"}</dd></div>
                <div><dt>Tedarikçi</dt><dd>{detail.supplierName || detail.supplier || "—"}{detail.supplierId ? "" : detail.supplier ? <small className="sm-sub"> (kart bağlı değil)</small> : null}</dd></div>
                <div><dt>Renk / ebat</dt><dd>{[detail.color, detail.size].filter(Boolean).join(" · ") || "—"}</dd></div>
                {detail.description && <div className="wide"><dt>Açıklama</dt><dd>{detail.description}</dd></div>}
              </dl>
              <div className="sm-actions">
                {can("movements", "create") && <>
                  <button className="sm-btn primary" onClick={() => openMove(detail, StockMovementType.Giris)}>{I.arrowIn} Giriş</button>
                  <button className="sm-btn" onClick={() => openMove(detail, StockMovementType.Cikis)}>Çıkış</button>
                  <button className="sm-btn" onClick={() => openMove(detail, StockMovementType.Sayim)}>Sayım</button>
                </>}
                {can("stocks", "update") && <button className="sm-btn" onClick={() => openEditStock(detail)}>{I.edit} Düzenle</button>}
              </div>
              <section className="sm-sec">
                <h3>Hareket geçmişi</h3>
                {detailLoading ? <div className="sm-loading"><span className="sm-spin" /> Yükleniyor</div>
                  : detailMoves.length === 0 ? <p className="sm-form-hint">Bu kart için hareket kaydı yok.</p>
                  : <div className="sm-table-wrap tight"><table className="sm-table small"><thead><tr><th>Tarih</th><th>Tür</th><th className="num">Miktar</th><th className="num hide-md">Birim fiyat</th><th className="num hide-sm">Tutar</th><th className="hide-md">Referans</th><th className="hide-md">Atölye</th><th className="hide-lg">Kişi</th><th className="hide-lg">Açıklama</th></tr></thead><tbody>{detailMoves.map((m) => moveRow(m, false))}</tbody></table></div>}
              </section>
            </div>
          </aside>
        </div>
      )}

      {/* ===== Stok kartı modalı ===== */}
      {stockModal.open && (
        <div className="sm-overlay center" onClick={() => setStockModal((m) => ({ ...m, open: false }))}>
          <form className="sm-modal wide" onClick={(e) => e.stopPropagation()} onSubmit={saveStock}>
            <div className="sm-modal-head"><div><span className="sm-kicker">Stok kartı</span><h3>{stockModal.editing ? "Kartı düzenle" : "Yeni stok kartı"}</h3></div><button type="button" className="sm-ib big" aria-label="Kapat" onClick={() => setStockModal((m) => ({ ...m, open: false }))}>{I.close}</button></div>
            <div className="sm-modal-body">
              <div className="sm-form-sec"><h4>Kimlik</h4><div className="sm-grid3">
                <Field label="Ürün kodu" hint="zorunlu"><input value={stockModal.form.productCode} onChange={(e) => setSF({ productCode: e.target.value })} placeholder="KMS-001" autoFocus /></Field>
                <Field label="Ürün adı" hint="zorunlu" span={2}><input value={stockModal.form.productName} onChange={(e) => setSF({ productName: e.target.value })} placeholder="Poplin kumaş beyaz" /></Field>
                <Field label="Açıklama" span={3}><input value={stockModal.form.description} onChange={(e) => setSF({ description: e.target.value })} placeholder="İsteğe bağlı" /></Field>
              </div></div>
              <div className="sm-form-sec"><h4>Sınıflandırma</h4><div className="sm-grid3">
                <Field label="Kategori" hint="zorunlu"><select value={stockModal.form.categoryId} onChange={(e) => setSF({ categoryId: e.target.value })}><option value="">Seçin</option>{categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></Field>
                <Field label="Stok tipi"><select value={stockModal.form.stockType} onChange={(e) => setSF({ stockType: Number(e.target.value) as StockType })}>{([StockType.Hammadde, StockType.YariMamul, StockType.Mamul] as StockType[]).map((t) => <option key={t} value={t}>{TYPE[t].label}</option>)}</select></Field>
                <Field label="Birim"><select value={stockModal.form.unit} onChange={(e) => setSF({ unit: e.target.value })}>{!units.some((u) => unitName(u) === stockModal.form.unit) && stockModal.form.unit && <option value={stockModal.form.unit}>{stockModal.form.unit}</option>}{units.map((u) => <option key={u.id} value={unitName(u)}>{unitName(u)}{u.abbreviation ? ` (${u.abbreviation})` : ""}</option>)}</select></Field>
              </div></div>
              <div className="sm-form-sec"><h4>Miktar ve seviye</h4><div className="sm-grid3">
                <Field label="Mevcut stok" hint={stockModal.editing ? "hareket kaydı önerilir" : "açılış miktarı"}><input type="number" step="0.001" min={0} value={stockModal.form.currentStock || ""} placeholder="0" onChange={(e) => setSF({ currentStock: Number(e.target.value) })} /></Field>
                <Field label="Minimum" hint="kritik uyarı eşiği"><input type="number" step="0.001" min={0} value={stockModal.form.minimumStock || ""} placeholder="0" onChange={(e) => setSF({ minimumStock: Number(e.target.value) })} /></Field>
                <Field label="Maksimum" hint="isteğe bağlı"><input type="number" step="0.001" min={0} value={stockModal.form.maximumStock || ""} placeholder="0" onChange={(e) => setSF({ maximumStock: Number(e.target.value) })} /></Field>
              </div>{stockModal.editing && <p className="sm-form-hint">Mevcut miktarı buradan değiştirmek hareket geçmişine yansımaz; sayım ya da giriş hareketi kaydetmek izlenebilirliği korur.</p>}</div>
              <div className="sm-form-sec"><h4>Fiyat</h4><div className="sm-grid3">
                <Field label="Birim fiyat"><input type="number" step="0.0001" min={0} value={stockModal.form.unitPrice || ""} placeholder="0" onChange={(e) => setSF({ unitPrice: Number(e.target.value) })} /></Field>
                <Field label="Para birimi"><select value={stockModal.form.currency} onChange={(e) => setSF({ currency: e.target.value })}>{CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}</select></Field>
              </div></div>
              <div className="sm-form-sec"><h4>Depo ve nitelikler</h4><div className="sm-grid3">
                <Field label="Depo konumu"><input value={stockModal.form.warehouseLocation} onChange={(e) => setSF({ warehouseLocation: e.target.value })} placeholder="A-12-3" /></Field>
                <Field label="Tedarikçi" hint="Satın Alma tedarikçi kartı" span={2}>
                  <div className="sm-supplier-pick">
                    <select value={stockModal.form.supplierId} onChange={(e) => { const id = e.target.value; const sup = suppliers.find((x) => x.supplierId === id); setSF({ supplierId: id, supplier: sup ? sup.name : stockModal.form.supplier }); }}>
                      <option value="">{stockModal.form.supplier && !stockModal.form.supplierId ? `Bağlı değil (metin: ${stockModal.form.supplier})` : "Seçilmedi"}</option>
                      {suppliers.map((x) => <option key={x.supplierId} value={x.supplierId}>{x.name}{x.code ? ` (${x.code})` : ""}</option>)}
                    </select>
                    {can("stocks", "create") && <button type="button" className="sm-btn" onClick={() => setNewSupplier({ open: !newSupplier.open, name: stockModal.form.supplier && !stockModal.form.supplierId ? stockModal.form.supplier : "", busy: false })} title="Yeni tedarikçi ekle">{I.plus}</button>}
                  </div>
                  {newSupplier.open && (
                    <div className="sm-supplier-new">
                      <input value={newSupplier.name} placeholder="Tedarikçi adı" onChange={(e) => setNewSupplier((n) => ({ ...n, name: e.target.value }))} onKeyDown={async (e) => { if (e.key === "Enter") { e.preventDefault(); await addSupplier(); } }} />
                      <button type="button" className="sm-btn primary small" disabled={newSupplier.busy || newSupplier.name.trim().length < 2} onClick={addSupplier}>{newSupplier.busy ? "…" : "Ekle"}</button>
                      <small>Ayrıntılar (vergi no, iletişim, vade) Stok &gt; Tedarikçiler sayfasından girilir.</small>
                    </div>
                  )}
                </Field>
                <Field label="Renk"><input value={stockModal.form.color} onChange={(e) => setSF({ color: e.target.value })} /></Field>
                <Field label="Boyut / ebat"><input value={stockModal.form.size} onChange={(e) => setSF({ size: e.target.value })} placeholder="150 cm en" /></Field>
                <Field label="Barkod"><input value={stockModal.form.barcode} onChange={(e) => setSF({ barcode: e.target.value })} /></Field>
              </div></div>
            </div>
            <div className="sm-modal-foot"><button type="button" className="sm-btn" onClick={() => setStockModal((m) => ({ ...m, open: false }))}>Vazgeç</button><button type="submit" className="sm-btn primary" disabled={busy}>{busy ? "Kaydediliyor" : stockModal.editing ? "Kaydet" : "Kartı oluştur"}</button></div>
          </form>
        </div>
      )}

      {/* ===== Kategori modalı ===== */}
      {catModal.open && (
        <div className="sm-overlay center" onClick={() => setCatModal((m) => ({ ...m, open: false }))}>
          <form className="sm-modal" onClick={(e) => e.stopPropagation()} onSubmit={saveCat}>
            <div className="sm-modal-head"><div><span className="sm-kicker">Kategori</span><h3>{catModal.editing ? "Kategoriyi düzenle" : "Yeni kategori"}</h3></div><button type="button" className="sm-ib big" aria-label="Kapat" onClick={() => setCatModal((m) => ({ ...m, open: false }))}>{I.close}</button></div>
            <div className="sm-modal-body">
              <Field label="Kategori adı" hint="zorunlu"><input value={catModal.name} onChange={(e) => setCatModal((m) => ({ ...m, name: e.target.value }))} placeholder="Kumaş, İplik, Aksesuar…" autoFocus /></Field>
              <Field label="Açıklama"><input value={catModal.description} onChange={(e) => setCatModal((m) => ({ ...m, description: e.target.value }))} placeholder="İsteğe bağlı" /></Field>
            </div>
            <div className="sm-modal-foot"><button type="button" className="sm-btn" onClick={() => setCatModal((m) => ({ ...m, open: false }))}>Vazgeç</button><button type="submit" className="sm-btn primary" disabled={busy}>{busy ? "Kaydediliyor" : "Kaydet"}</button></div>
          </form>
        </div>
      )}

      {/* ===== Birim modalı ===== */}
      {unitModal.open && (
        <div className="sm-overlay center" onClick={() => setUnitModal((m) => ({ ...m, open: false }))}>
          <form className="sm-modal" onClick={(e) => e.stopPropagation()} onSubmit={saveUnit}>
            <div className="sm-modal-head"><div><span className="sm-kicker">Ölçü birimi</span><h3>{unitModal.editing ? "Birimi düzenle" : "Yeni birim"}</h3></div><button type="button" className="sm-ib big" aria-label="Kapat" onClick={() => setUnitModal((m) => ({ ...m, open: false }))}>{I.close}</button></div>
            <div className="sm-modal-body">
              <div className="sm-grid3">
                <Field label="Birim adı" hint="zorunlu" span={2}><input value={unitModal.unitName} onChange={(e) => setUnitModal((m) => ({ ...m, unitName: e.target.value }))} placeholder="Metre" autoFocus /></Field>
                <Field label="Kısaltma"><input value={unitModal.abbreviation} maxLength={10} onChange={(e) => setUnitModal((m) => ({ ...m, abbreviation: e.target.value }))} placeholder="mt" /></Field>
                <Field label="Sıra" hint="listede görünüm sırası"><input type="number" min={0} value={unitModal.sortOrder} onChange={(e) => setUnitModal((m) => ({ ...m, sortOrder: Number(e.target.value) }))} /></Field>
              </div>
            </div>
            <div className="sm-modal-foot"><button type="button" className="sm-btn" onClick={() => setUnitModal((m) => ({ ...m, open: false }))}>Vazgeç</button><button type="submit" className="sm-btn primary" disabled={busy}>{busy ? "Kaydediliyor" : "Kaydet"}</button></div>
          </form>
        </div>
      )}

      {/* ===== Hareket modalı ===== */}
      {moveModal.open && (
        <div className="sm-overlay center" onClick={() => setMoveModal((m) => ({ ...m, open: false }))}>
          <form className="sm-modal wide" onClick={(e) => e.stopPropagation()} onSubmit={saveMove}>
            <div className="sm-modal-head"><div><span className="sm-kicker">Stok hareketi</span><h3>{moveStock ? moveStock.productName : "Yeni hareket"}</h3></div><button type="button" className="sm-ib big" aria-label="Kapat" onClick={() => setMoveModal((m) => ({ ...m, open: false }))}>{I.close}</button></div>
            <div className="sm-modal-body">
              <Field label="Stok kartı" hint="zorunlu">
                <select value={moveModal.form.stockId} onChange={(e) => { const s = stockById.get(e.target.value); setMF({ stockId: e.target.value, unitPrice: s?.unitPrice || 0, currency: s?.currency || "TRY", fromWarehouse: s?.warehouseLocation || "" }); }} autoFocus={!moveModal.form.stockId}>
                  <option value="">Seçin</option>
                  {stocks.map((s) => <option key={s.id} value={s.id}>{s.productCode} · {s.productName} ({qty(s.currentStock)} {s.unit})</option>)}
                </select>
              </Field>
              <div className="sm-movetypes">
                {(Object.keys(MOVE).map(Number) as StockMovementType[]).map((t) => (
                  <button type="button" key={t} className={`sm-movetype ${MOVE[t].tone} ${moveModal.form.movementType === t ? "on" : ""}`} onClick={() => setMF({ movementType: t })}>{MOVE[t].label}</button>
                ))}
              </div>
              <p className="sm-form-hint">{MOVE[moveModal.form.movementType].hint}</p>
              <div className="sm-grid3">
                <Field label={moveModal.form.movementType === StockMovementType.Sayim ? "Sayılan miktar" : "Miktar"} hint={moveStock ? moveStock.unit : undefined}><input type="number" step="0.001" min={0} value={moveModal.form.quantity} onChange={(e) => setMF({ quantity: Number(e.target.value) })} autoFocus={!!moveModal.form.stockId} /></Field>
                <Field label="Birim fiyat"><input type="number" step="0.0001" min={0} value={moveModal.form.unitPrice || ""} placeholder="0" onChange={(e) => setMF({ unitPrice: Number(e.target.value) })} /></Field>
                <Field label="Para birimi"><select value={moveModal.form.currency} onChange={(e) => setMF({ currency: e.target.value })}>{CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}</select></Field>
              </div>
              {movePreview && (
                <div className={`sm-preview ${movePreview.insufficient ? "bad" : ""}`}>
                  <span>Mevcut <b>{qty(movePreview.cur)} {movePreview.unit}</b></span><span className="arrow">→</span><span>Sonra <b>{qty(movePreview.after)} {movePreview.unit}</b></span>
                  <span className="tail">{movePreview.insufficient ? "Yetersiz stok, kayıt reddedilir" : `Tutar ${money((Number(moveModal.form.quantity) || 0) * (Number(moveModal.form.unitPrice) || 0), moveModal.form.currency)}`}</span>
                </div>
              )}
              {moveModal.form.movementType === StockMovementType.Transfer && (
                <div className="sm-grid3">
                  <Field label="Kaynak depo"><input value={moveModal.form.fromWarehouse} onChange={(e) => setMF({ fromWarehouse: e.target.value })} /></Field>
                  <Field label="Hedef depo" span={2}><input value={moveModal.form.toWarehouse} onChange={(e) => setMF({ toWarehouse: e.target.value })} /></Field>
                </div>
              )}
              <div className="sm-grid3">
                <Field label="Referans no"><input value={moveModal.form.referenceNo} onChange={(e) => setMF({ referenceNo: e.target.value })} placeholder="IRS-2026-001" /></Field>
                <Field label="Referans türü"><input value={moveModal.form.referenceType} onChange={(e) => setMF({ referenceType: e.target.value })} placeholder="İrsaliye, sipariş…" /></Field>
                <Field label="Tarih ve saat"><input type="datetime-local" value={moveModal.form.movementDate} onChange={(e) => setMF({ movementDate: e.target.value })} /></Field>
                <Field label="İşlemi yapan"><input value={moveModal.form.movedBy} onChange={(e) => setMF({ movedBy: e.target.value })} /></Field>
                <Field label="Açıklama" span={2}><input value={moveModal.form.description} onChange={(e) => setMF({ description: e.target.value })} /></Field>
              </div>
            </div>
            <div className="sm-modal-foot"><button type="button" className="sm-btn" onClick={() => setMoveModal((m) => ({ ...m, open: false }))}>Vazgeç</button><button type="submit" className="sm-btn primary" disabled={busy || !moveModal.form.stockId || !!movePreview?.insufficient}>{busy ? "Kaydediliyor" : "Hareketi kaydet"}</button></div>
          </form>
        </div>
      )}

      <AlertModal isOpen={alert.isOpen} message={alert.message} type={alert.type} onClose={() => setAlert((a) => ({ ...a, isOpen: false }))} />
      <ConfirmModal isOpen={confirm.isOpen} message={confirm.message} type="danger" onConfirm={confirm.onConfirm} onCancel={() => setConfirm((c) => ({ ...c, isOpen: false }))} />
    </div>
  );
};

export default StockManagement;
